import React from 'react';
import './contentComponents.css';

const getInitials = (name) => name
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0])
  .join('')
  .toUpperCase();

const Role = ({ role }) => (
  <div className="person-card__role">
    <span>{role.title}</span>
    {role.periodLabel && <span> ({role.periodLabel})</span>}
    {role.institution && <span>, {role.institution}</span>}
    {role.coAdvisedWith && <span> (Co-advised with {role.coAdvisedWith})</span>}
  </div>
);

const Contribution = ({ contribution }) => {
  const [primaryLink, ...otherLinks] = contribution.links || [];

  return (
    <li className="person-card__contribution">
      <strong className="person-card__contribution-title">
        {primaryLink ? (
          <a href={primaryLink.url}>{contribution.title}</a>
        ) : contribution.title}
      </strong>
      {contribution.date && <time dateTime={contribution.date}> ({contribution.date})</time>}
      {contribution.description && <div>{contribution.description}</div>}
      {otherLinks.map((link) => (
        <a className="content-link" href={link.url} key={`${contribution.id}-${link.label}`}>
          {link.label}
        </a>
      ))}
    </li>
  );
};

const PersonCard = ({
  person,
  compact = false,
  showContributions = true,
  showHighlight = true,
  showLinks = true,
}) => {
  if (!person) return null;

  const profileLink = (person.links || []).find(({ label, type }) => (
    type === 'homepage' || label.toLowerCase() === 'homepage'
  )) || (person.links || []).find(({ type }) => type === 'linkedin');
  const profileDestination = profileLink && profileLink.type === 'linkedin' ? 'profile' : 'homepage';
  const className = compact ? 'content-card person-card person-card--compact' : 'content-card person-card';

  return (
    <article className={`${className}${profileLink ? ' person-card--linked' : ''}`}>
      {profileLink && (
        <a
          className="person-card__overlay-link"
          href={profileLink.url}
          aria-label={`Visit ${person.name}'s ${profileDestination}`}
        >
          <span className="sr-only">Visit {person.name}'s {profileDestination}</span>
        </a>
      )}
      <div className="person-card__identity">
        {person.photo ? (
          <img className="person-card__photo" src={person.photo} alt={`Portrait of ${person.name}`} />
        ) : (
          <div className="person-card__photo person-card__photo--placeholder" aria-hidden="true">
            {getInitials(person.name)}
          </div>
        )}
        <div>
          <h3 className="content-card__title">{person.name}</h3>
          {(person.roles || []).slice(0, 1).map((role, index) => (
            <Role role={role} key={`${person.id}-${role.title}-${role.startDate}-${index}`} />
          ))}
        </div>
      </div>

      {showHighlight && person.highlight && <p className="person-card__highlight">{person.highlight}</p>}

      {showContributions && person.contributions && person.contributions.length > 0 && (
        <section className="person-card__contributions" aria-label={`${person.name} highlights`}>
          <ul>
            {person.contributions.slice(0, 2).map((contribution) => (
              <Contribution contribution={contribution} key={contribution.id} />
            ))}
          </ul>
        </section>
      )}

      {showLinks && person.links && person.links.length > 0 && (
        <nav className="content-links" aria-label={`${person.name} links`}>
          {person.links.map((link) => (
            <a href={link.url} key={`${person.id}-${link.label}`}>
              {link.label || 'Homepage'}
            </a>
          ))}
        </nav>
      )}
    </article>
  );
};

export default PersonCard;
